/**
 * Weekly-rollup status vocabulary — one definition for every surface.
 *
 * These labels lived as near-copies in WeeklyRollupsTab, PcoDrawer and
 * RollupDetailPage, and they had already drifted: a sent week read "Submitted"
 * on the PCO page and "Pending" on the rollups tab. On 2026-07-29 Brandon sent
 * the project's largest week to date ($871,550.28) and the row came back
 * grey-chipped "PENDING" — he read it as not sent. One map, imported
 * everywhere, is what stops that recurring.
 *
 * `submitted` means the package left the building. It is not a waiting state
 * from Coffman's side; the GC's approval is the pending thing, and that has
 * its own label. Displayed as "Sent" (client, 2026-08-01): the Weekly Rollups
 * page groups by Open / Sent / Approved, and the status word must match the
 * pile word — two vocabularies for one fact is how the 7/29 misread happened.
 * Kind stays the accent 'progress' rather than grey 'pending', which read
 * as inert. (The money stat "Submitted to date" is a different concept —
 * Coffman's own spreadsheet vocabulary — and deliberately keeps its name.)
 */
import type { RollupStatus } from '@coffman-tm/shared';
import type { StatusPillKind } from '../components/design';

export function rollupStatusLabel(status: RollupStatus): string {
  switch (status) {
    case 'in_progress':
      return 'In progress';
    case 'submitted':
      return 'Sent';
    case 'approved':
      return 'Approved';
    case 'paid':
      return 'Paid';
    default:
      return status;
  }
}

export function rollupPillKind(status: RollupStatus): StatusPillKind {
  switch (status) {
    case 'in_progress':
    case 'submitted':
      return 'progress';
    case 'approved':
    case 'paid':
      return 'ready';
    default:
      return 'neutral';
  }
}

/**
 * "Submitted 7/29" for a chip subline, or null when the payload carries no
 * timestamp (imported history, or a status that never had one). Takes the ISO
 * instant the API already sends on the rollup — no new API surface.
 */
export function sentOnLabel(submittedAt: string | null | undefined): string | null {
  if (!submittedAt) return null;
  const day = submittedAt.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/**
 * THE GC IS HOLDING AN OLDER COPY — and the date on it.
 *
 * A week can reach `submitted` two ways now: an Approve & Send, or **Close
 * without sending** (2026-08-06), which the PM uses when he reopened a sent
 * week to take hours off and the GC does not want a second email for it. Both
 * end with the week standing as the version of record; only one of them put a
 * copy of that version in the GC's inbox.
 *
 * The difference is DERIVED, not stored. `weekly_rollups.submitted_at` means
 * "when a copy of THIS version reached the GC" — a send stamps it, a reopen
 * nulls it, and the close deliberately leaves it null. So a week that is no
 * longer in progress, carries no `submitted_at`, and HAS sends behind it is
 * exactly the "closed without sending" state, and the newest of those sends is
 * the copy the GC actually has. A later real send re-stamps the column and
 * this returns null with nothing to clean up.
 *
 * Returns the ISO instant of the GC's copy, or null when there is no such
 * claim to make: an in-progress week (not the version of record yet), a week
 * whose current version really was emailed, or one that has never been sent.
 *
 * Order of `sends` is not trusted — the newest is picked here — so a caller
 * that fetched them any which way still gets the right date.
 *
 * IT CAN LIE IN TWO NARROW WINDOWS, both because the server sets `status` and
 * `submitted_at` in separate writes: transiently, for the sub-second a
 * successful resend is in flight, and persistently after a post-send write
 * failure (already an ops-paged incident). Both close if `submitted_at` ever
 * moves into `submitRollup`'s atomic claim. Full story, with severities and
 * the fix shape: `docs/system/11-intentional.md` §L3.
 */
export function closedWithoutSendAt(
  rollup: { status: RollupStatus; submitted_at: string | null },
  sends: readonly { submitted_at: string }[],
): string | null {
  if (rollup.status === 'in_progress') return null;
  if (rollup.submitted_at) return null;
  return latestSendAt(sends);
}

/**
 * The newest send in a history list, or null for a week never sent. Order is
 * not trusted (the route serves newest-first, but a caller that sorted it any
 * other way must still get the same answer). ISO-8601 instants compare
 * correctly as strings; every one of these comes from `Date#toISOString`.
 */
export function latestSendAt(sends: readonly { submitted_at: string }[]): string | null {
  let newest: string | null = null;
  for (const s of sends) {
    if (!s?.submitted_at) continue;
    if (newest === null || s.submitted_at > newest) newest = s.submitted_at;
  }
  return newest;
}
